/**
 * Option lists for the /tracks Instruments, Key, Language and Label filters.
 *
 * Every option is deterministic: the first three are constants derived from the live catalog, and
 * labels come from the `labels` table. An option is an `id` (what the page stores and shows in a
 * chip), a `label` (what the menu shows) and the stored `values` it matches. `applyTrackFilters`
 * (catalog.ts) turns the selected ids into the query through `facetValues`.
 */
import { supabase } from "./supabase";
import { getExcludedLabels } from "./excluded-labels"; // [EXCLUDE_LABELS]
import { labelImageUrl } from "./labels";

export interface FacetOption {
  id: string;
  label: string;
  values: readonly string[];
}

/**
 * Instrument families, each matched by `.overlaps("instruments", …)` (any of its spellings).
 *
 * `tracks.instruments` is free AI-generated text: 1,726 distinct values across 88,709 published
 * tracks on 2026-10-12. Each family lists every stored spelling used on at least 20 published main
 * works (parent_track_id null), read by paging the published tracks read-only (GET
 * `tracks?select=instruments,parent_track_id&is_published=eq.true`, ordered by id, 1,000 rows a
 * page). That covers 96–99.6% of each family's mentions. "Vocals" is not offered: no real main work
 * lists it (vocals live in `vocal_type`).
 *
 * The spellings are exact, so regenerate this list the same way when new spellings appear. A value
 * must never contain `,` `{` `}` `"` or `\`: postgrest-js sends the array unquoted as `ov.{a,b}`.
 */
export const INSTRUMENT_OPTIONS: readonly FacetOption[] = [
  {
    id: "Guitar",
    label: "Guitar",
    values: [
      "Electric guitar", "Acoustic guitar", "Electric Guitar", "Acoustic Guitar", "electric guitar",
      "Slide guitar", "Classical guitar", "Nylon-string guitar", "Nylon-string acoustic guitar",
    ],
  },
  {
    id: "Bass",
    label: "Bass",
    values: [
      "Bass guitar", "Electric bass", "Synth bass", "Bass synthesizer", "Sub-bass", "808 Bass",
      "Upright bass", "Double Bass", "Bass synth", "Electric Bass", "Bass Guitar", "808 bass",
      "Double bass", "Synth Bass", "Acoustic bass", "Bass", "bass guitar", "Upright Bass",
      "Sub-bass synthesizer", "Bass Synthesizer", "Electric bass guitar", "Electronic bass",
      "Bass Synth", "Synthesizer bass", "Acoustic Bass", "Digital bass", "Sub-bass synth",
      "Electronic Bass", "Acoustic bass guitar", "electric bass", "Distorted bass", "Slap bass",
      "synth bass",
    ],
  },
  {
    id: "Piano & keys",
    label: "Piano & keys",
    values: [
      "Piano", "Electric piano", "Acoustic piano", "Harpsichord", "Organ", "Harmonium", "Grand piano",
      "Keyboard", "Hammond organ", "Rhodes electric piano", "Acoustic Piano", "Electric Piano",
      "Digital piano", "Celesta", "Pipe organ", "Felt piano", "Grand Piano", "Digital Piano",
      "Rhodes piano", "piano", "Keyboards",
    ],
  },
  {
    id: "Drums",
    label: "Drums",
    values: [
      "Drum machine", "Drums", "Drum kit", "Electronic drums", "Drum Machine", "Cymbals",
      "Acoustic drums", "Electronic Drums", "Snare drum", "Kick drum", "Bass drum", "Drum Kit",
      "Snare Drum", "Bass Drum", "Taiko drums", "Frame drum", "Acoustic drum kit", "drum kit",
      "Hand drums", "Hi-hats", "Cymbal", "drums", "Hi-hat", "Taiko Drums", "Hand drum",
      "Acoustic Drums", "Frame Drum", "Log drum", "Talking drum", "Taiko drum",
    ],
  },
  {
    id: "Percussion",
    label: "Percussion",
    values: [
      "Percussion", "Shaker", "Electronic percussion", "Handclaps", "Timpani", "Tambourine", "Tabla",
      "Glockenspiel", "Dholak", "Congas", "Woodblock", "Dhol", "Shakers", "Digital percussion",
      "Electronic Percussion", "Cowbell", "Orchestral percussion", "Orchestral Percussion", "Darbuka",
      "Bongos", "Taiko drums", "Frame drum", "Hand percussion", "Triangle", "Timbales",
      "Digital Percussion", "Bells", "Sleigh bells", "Cinematic percussion", "Claps", "Vibraphone",
      "Cinematic Percussion", "Guiro", "Xylophone", "Marimba", "Taiko Drums", "Djembe",
      "Percussion (electronic)", "Metallic percussion", "Frame Drum", "Hand claps", "Cajon",
      "Industrial percussion", "Clap", "Woodblocks", "percussion", "Taiko drum",
    ],
  },
  {
    id: "Strings",
    label: "Strings",
    values: [
      "Violin", "Cello", "Strings", "Viola", "Double Bass", "Orchestral strings", "Double bass",
      "String section", "Harp", "Pizzicato strings", "String ensemble", "Orchestral Strings",
      "Violins", "Fiddle", "Cellos", "String Section", "Violas", "Double Basses", "Contrabass",
      "String orchestra", "String Ensemble", "Bowed strings", "Pizzicato Strings", "strings",
    ],
  },
  {
    id: "Brass",
    label: "Brass",
    values: [
      "Trumpet", "Trombone", "French Horn", "Brass", "French horn", "Tuba", "Brass section",
      "Trombones", "French Horns", "Trumpets", "Brass ensemble", "Brass Section", "Horns",
    ],
  },
  {
    id: "Woodwinds",
    label: "Woodwinds",
    values: [
      "Flute", "Saxophone", "Clarinet", "Oboe", "Bassoon", "Woodwinds", "Bansuri", "Tenor saxophone",
      "Pan flute", "Flutes", "Oboes", "Shakuhachi", "Clarinets", "Piccolo", "Bassoons", "Ney",
      "Duduk", "Tenor Saxophone",
    ],
  },
  {
    id: "Synthesizer",
    label: "Synthesizer",
    values: [
      "Synthesizer", "Synth bass", "Bass synthesizer", "Bass synth", "Digital pads", "Arpeggiator",
      "Synth Bass", "Sequencer", "Synth pad", "Synthesizer pad", "Sub-bass synthesizer",
      "Bass Synthesizer", "synthesizer", "Analog synthesizer", "Pads", "Synth strings", "Bass Synth",
      "Synth pads", "Synthesizer bass", "Digital Pads", "Synth Pad", "String pad",
      "Synthesizer pads", "Analog Synthesizer", "String pads", "Pad synthesizer", "Sub-bass synth",
      "Synth brass", "Ambient pads", "Synth Pads", "Digital Synthesizer", "Synthesizers",
      "synth bass", "Electronic pads", "Synthesizer Pad", "Synth Strings", "Ambient Pads",
    ],
  },
];

/** The twelve pitch classes, each with every spelling the catalog stores for it. */
const PITCH_CLASSES: readonly (readonly string[])[] = [
  ["C"], ["C#", "Db"], ["D"], ["D#", "Eb"], ["E"], ["F"],
  ["F#", "Gb"], ["G"], ["G#", "Ab"], ["A"], ["A#", "Bb"], ["B"],
];

function keyOption(spellings: readonly string[], mode: "major" | "minor"): FacetOption {
  const label = `${spellings.join(" / ")} ${mode}`;
  return {
    id: label,
    label,
    // Analysed tracks store "C# major" / "C# minor"; the draft editor (MUSIC_KEYS) writes "C#" / "C#m".
    values: spellings.flatMap((s) => [`${s} ${mode}`, mode === "major" ? s : `${s}m`]),
  };
}

/**
 * The 24 keys, majors then minors, matched by `.in("key", …)`. One option per pitch class covers
 * both enharmonic spellings: the catalog stores "C# major" for some tracks and "Db major" for
 * others, and an option for only one of them hid about 14% of the keyed catalog.
 */
export const KEY_OPTIONS: readonly FacetOption[] = [
  ...PITCH_CLASSES.map((spellings) => keyOption(spellings, "major")),
  ...PITCH_CLASSES.map((spellings) => keyOption(spellings, "minor")),
];

const languageOption = (label: string, values: readonly string[] = [label]): FacetOption => ({
  id: label,
  label,
  values,
});

/**
 * Languages, matched as a substring of `tracks.language` (`ILIKE '%value%'`), the rule Sol's
 * `match_tracks_v3` uses: the column holds free text such as "English, Arabic".
 *
 * A fixed list rather than one read from the catalog, because the column also holds placeholders
 * ("None" on instrumentals, "", "[None]", "Unknown") that nobody filters for. These are the 32
 * languages on at least 20 real published main works on 2026-10-12, alphabetical. Together they
 * cover 99.2% of the works with a real language value. Values must not contain `,` `(` or `)`:
 * they are joined into one PostgREST `.or()` filter.
 */
export const LANGUAGE_OPTIONS: readonly FacetOption[] = [
  languageOption("Amharic"),
  languageOption("Arabic"),
  languageOption("Chinese", ["Chinese", "Mandarin", "Cantonese"]),
  languageOption("English"),
  languageOption("French"),
  languageOption("German"),
  languageOption("Greek"),
  languageOption("Gujarati"),
  languageOption("Haitian Creole"),
  languageOption("Hebrew"),
  languageOption("Hindi"),
  languageOption("Indonesian"),
  languageOption("Italian"),
  languageOption("Japanese"),
  languageOption("Korean"),
  languageOption("Latin"),
  languageOption("Lingala"),
  languageOption("Marathi"),
  languageOption("Portuguese"),
  languageOption("Punjabi"),
  languageOption("Russian"),
  languageOption("Sanskrit"),
  languageOption("Spanish"),
  languageOption("Tagalog"),
  languageOption("Tamil"),
  languageOption("Thai"),
  languageOption("Turkish"),
  languageOption("Urdu"),
  languageOption("Vietnamese"),
  languageOption("Wolof"),
  languageOption("Yoruba"),
  languageOption("Zulu"),
];

/** The stored values the selected options match: a deduped union in option order. Unknown ids are ignored. */
export function facetValues(options: readonly FacetOption[], selectedIds: readonly string[]): string[] {
  const selected = new Set(selectedIds);
  const values = new Set<string>();
  for (const option of options) {
    if (!selected.has(option.id)) continue;
    for (const value of option.values) values.add(value);
  }
  return [...values];
}

/** The display label for an option id, or the id itself when no option has it. */
export function facetLabel(options: readonly FacetOption[], id: string): string {
  return options.find((option) => option.id === id)?.label ?? id;
}

/** A Label filter option, with what the home page's "Browse by org" card shows of the label. */
export interface LabelFilterOption extends FacetOption {
  /** `labels.cover_image` (the label's logo) when it is a usable image URL, else null. */
  logoUrl: string | null;
  /** The owning organization's `type`; null when the label has none or the viewer cannot read it. */
  orgType: string | null;
}

/**
 * Label filter options: every label with at least one published main work, by name, minus the
 * labels excluded from buyers' catalog (VITE_EXCLUDED_LABELS). The same `labels` table the Albums
 * and Artists pages read; `tracks!inner` with a one-row embed only checks that such a track exists.
 * A label option matches `tracks.label_tag` exactly, so its id is its only value. The logo and the
 * organization's type ride along for the home page's org cards (one list, two surfaces).
 */
export async function fetchLabelFilterOptions(): Promise<LabelFilterOption[]> {
  let query = supabase
    .from("labels")
    .select("id, name, cover_image, organizations(type), tracks!inner(id)")
    .eq("tracks.is_published", true)
    .is("tracks.parent_track_id", null)
    .limit(1, { referencedTable: "tracks" });
  // Left out by the server, so their rows (one test label's logo is a ~100 KB data URI) are never sent.
  const excluded = getExcludedLabels(); // [EXCLUDE_LABELS]
  if (excluded.length > 0) query = query.not("id", "in", `(${excluded.join(",")})`); // [EXCLUDE_LABELS]
  const { data, error } = await query.order("name", { ascending: true });
  if (error) throw error;

  return (data ?? []).map((row) => ({
      id: row.id,
      label: row.name,
      values: [row.id],
      logoUrl: labelImageUrl(row.cover_image),
      orgType: row.organizations?.type ?? null,
    }));
}
